import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaPause, FaPlay } from 'react-icons/fa';
import { getAudioFile } from 'services/devices/devices.service';

const AudioPreview = ({
  file,
  type,
  tone,
  roadStatus,
  setRoadStatus,
  playButtonOverride,
  pauseButtonOverride,
  buttonClassName = 'btn btn-primary p-2',
  buttonStyle,
  update,
  ariaLabel = 'Preview audio',
  showTimeline = false,
  durationSeconds = 0,
}) => {
  // console.log('First line in audioPreview');
  const audioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [audioValid, setAudioValid] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds);

  useEffect(() => {
    let currentAudio = null;
    let blobUrl = null;

    // console.log('creating Audio Previw');

    async function getAudio() {
      try {
        let audioBlob;
        if (file instanceof Blob) {
          audioBlob = file;
        } else {
          if (!tone?.id) throw new Error('Missing tone id');
          const response = await getAudioFile(tone.id);

          // getAudioFile swallows its own errors and returns undefined on a failed fetch.
          if (!response?.data) {
            throw new Error(`Audio file ${tone.id} is not available from this backend`);
          }
          audioBlob = response.data;
        }

        blobUrl = URL.createObjectURL(audioBlob);

        currentAudio = new Audio(blobUrl);
        currentAudio.onloadedmetadata = () => {
          if (Number.isFinite(currentAudio.duration)) setDuration(currentAudio.duration);
        };
        currentAudio.ontimeupdate = () => setCurrentTime(currentAudio.currentTime);
        currentAudio.onended = () => {
          setIsPlaying(false);
          setCurrentTime(currentAudio.duration || durationSeconds);
        };
        currentAudio.onerror = (e) => {
          console.error('Audio playback error:', e);
          setAudioValid(false);
          audioRef.current = null;
        };

        audioRef.current = currentAudio;
        setAudioValid(true);
      } catch (err) {
        console.error('Error getting Audio playback file:', err);
        setAudioValid(false);
        audioRef.current = null;
      }
    }

    getAudio();

    // stop old audio when tone/update changes
    return () => {
      try {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
      } catch {}
      audioRef.current = null;
      setIsPlaying(false);
      setCurrentTime(0);
    };
  }, [durationSeconds, file, tone, update]);

  useEffect(() => {
    if (Number.isFinite(durationSeconds) && durationSeconds > 0) setDuration(durationSeconds);
  }, [durationSeconds]);

  const playTone = () => {
    if (!audioRef.current) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current
          .play()
          .then(() => setIsPlaying(true))
          .catch((error) => console.error('Error playing audio:', error));
      }
    } catch (error) {
      console.error('Error handling audio playback:', error);
    }
  };

  const formatTime = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
  };

  const playbackButton = (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={!audioValid}
      title={!audioValid ? 'Audio file not available from this backend' : undefined}
      className={buttonClassName + (!audioValid ? ' disabled' : '')}
      style={buttonStyle}
      onClick={audioValid ? playTone : undefined}
    >
      {isPlaying ? (
        <>
          {!pauseButtonOverride && (
            <div className="d-flex gap-2">
              <div>
                <FaPause />
              </div>
              <p>Preview</p>
            </div>
          )}

          {pauseButtonOverride}
        </>
      ) : (
        <>
          {!playButtonOverride && (
            <div className="d-flex gap-2">
              <div>
                <FaPlay />
              </div>
              <div>Preview</div>
            </div>
          )}

          {playButtonOverride}
        </>
      )}
    </button>
  );

  if (showTimeline) {
    const resolvedDuration = Math.max(0, Number(duration) || 0);
    const remainingTime = Math.max(0, resolvedDuration - currentTime);

    return (
      <div className="d-flex align-items-center gap-2 flex-grow-1 min-width-0">
        {playbackButton}
        <div className="d-flex flex-column flex-grow-1 min-width-0">
          <input
            type="range"
            className="form-range"
            aria-label="Audio playback position"
            min="0"
            max={resolvedDuration || 0}
            step="0.1"
            value={Math.min(currentTime, resolvedDuration)}
            disabled={!audioValid || !resolvedDuration}
            onChange={(event) => {
              const nextTime = Number(event.target.value);
              if (!audioRef.current || !Number.isFinite(nextTime)) return;
              audioRef.current.currentTime = nextTime;
              setCurrentTime(nextTime);
            }}
          />
          <small className="d-flex justify-content-between gap-2">
            <span>{formatTime(currentTime)} elapsed</span>
            <span>{formatTime(remainingTime)} remaining</span>
          </small>
        </div>
      </div>
    );
  }

  return playbackButton;
};

export default AudioPreview;
